import {
  FormModal,
  FormModalType,
  InputField,
  Option,
  SelectInputField,
  useToast,
  noop,
} from '@chillibean/component-library';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { DisplayNameType, useDisplayName, useStudyProtocolModal } from '../../../../hooks';
import { StudyEventDefinition } from '../../../../models';
import {
  addStudyEventDefinition,
  modifyStudyEventDefinition,
  removeStudyEventDefinition,
} from '../../../../redux/actions';

export function StudyEventDefinitionModal(props) {
  const { onClose = noop, onSuccess = noop, studyEventDefinition = undefined, visible = false } = props;

  // VARIABLES
  const defaultValues = {
    displayName: studyEventDefinition?.displayName,
    identifier: studyEventDefinition?.identifier,
    position: studyEventDefinition?.position?.position,
    studyArmId: studyEventDefinition?.studyArm?.id,
  };

  // HOOKS
  // Store
  const dispatch = useDispatch();
  const { data: protocol } = useSelector((state) => state.protocolReducer.protocol);
  const { error: actionError, loading } = useSelector((state) => state.protocolReducer.protocolAction);
  // Custom
  const { getDisplayName } = useDisplayName();
  const { getCustomErrors, onCloseHandler } = useStudyProtocolModal({ onClose });
  const { showErrorToast, showSuccessToast } = useToast();

  const fields = [
    new InputField({
      disabled: loading,
      id: 'identifier',
      label: 'Identifier',
      maxLength: 250,
      name: 'Identifier',
      pattern: {
        message: 'Only capital letters and numbers allowed',
        value: /^[A-Z0-9]+$/,
      },
      required: true,
      transformValue: (value) => value.toUpperCase().replace(' ', ''),
      value: defaultValues.identifier,
    }),
    new InputField({
      disabled: loading,
      id: 'displayName',
      label: 'Name',
      maxLength: 250,
      name: 'Name',
      required: true,
      value: defaultValues.displayName,
    }),
    new SelectInputField({
      disabled: loading,
      id: 'studyArmId',
      label: 'Study Arm',
      name: 'Study Arm',
      options: protocol ? protocol.studyArms.map((studyArm) => new Option(studyArm.displayName, studyArm.id)) : [],
      value: defaultValues.studyArmId,
      withBlankOption: true,
    }),
    new SelectInputField({
      disabled: loading,
      id: 'position',
      label: 'Position',
      name: 'Position',
      options: protocol?.table?.positions.map((position) => new Option(position.name, position.position)),
      value: defaultValues.position,
      visible: () => Boolean(studyEventDefinition),
    }),
  ];

  // METHODS
  const getTitle = () => {
    if (studyEventDefinition) return `Update ${getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular}`;

    return `Add ${getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular}`;
  };

  const onCreate = (data) => {
    dispatch(
      addStudyEventDefinition({
        onError: showErrorToast,
        onSuccess: () => {
          showSuccessToast(`${getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular} created successfully`);
          onSuccess();
        },
        protocolId: protocol.id,
        studyEventDefinitionData: data,
      }),
    );
  };

  const onUpdate = (data) => {
    dispatch(
      modifyStudyEventDefinition({
        id: studyEventDefinition.id,
        onError: showErrorToast,
        onSuccess: () => {
          showSuccessToast(`${getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular} updated successfully`);
          onSuccess();
        },
        protocolId: protocol.id,
        studyEventDefinitionData: data,
      }),
    );
  };

  const onDelete = () => {
    dispatch(
      removeStudyEventDefinition({
        id: studyEventDefinition.id,
        onError: showErrorToast,
        onSuccess: () => {
          showSuccessToast(`${getDisplayName(DisplayNameType.STUDY_EVENT).humanized.singular} removed successfully`);
          onSuccess();
        },
        protocolId: protocol.id,
      }),
    );
  };

  return (
    <FormModal
      customErrors={getCustomErrors(actionError)}
      defaultValues={defaultValues}
      deletable={Boolean(studyEventDefinition)}
      fields={fields}
      loading={loading}
      onClose={onCloseHandler}
      onCreate={onCreate}
      onDelete={onDelete}
      onUpdate={onUpdate}
      title={getTitle()}
      type={studyEventDefinition ? FormModalType.UPDATE : FormModalType.CREATE}
      visible={visible}
    />
  );
}

StudyEventDefinitionModal.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  studyEventDefinition: PropTypes.instanceOf(StudyEventDefinition),
  visible: PropTypes.bool,
};

export default StudyEventDefinitionModal;
